import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { FileText, ScrollText } from "lucide-react";

export default function Terms() {
  const { lang } = useLang();
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <div className="overline text-secondary flex items-center gap-1.5"><ScrollText size={12} /> Legal</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          {lang === "hi" ? "नियम और शर्तें" : "Terms & Conditions"}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {lang === "hi" ? "अंतिम अपडेट: फ़रवरी 2026" : "Last updated: February 2026"}
        </p>
      </div>

      <div className="prose prose-sm max-w-none space-y-6 text-sm leading-relaxed" data-testid="terms-content">
        <section>
          <h2 className="text-lg font-semibold text-primary">1. {lang === "hi" ? "स्वीकृति" : "Acceptance"}</h2>
          <p>
            {lang === "hi"
              ? "BhoomiMitra (\"we\", \"us\") का उपयोग करके, आप इन शर्तों से बंधे रहने के लिए सहमत होते हैं। यदि आप सहमत नहीं हैं, तो कृपया प्लेटफ़ॉर्म का उपयोग न करें।"
              : "By using BhoomiMitra (\"we\", \"us\"), you agree to be bound by these Terms. If you do not agree, please do not use the platform."}
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">2. {lang === "hi" ? "प्लेटफ़ॉर्म की भूमिका" : "Role of the Platform"}</h2>
          <p>
            {lang === "hi"
              ? "BhoomiMitra खरीदारों और विक्रेताओं को जोड़ने वाला एक तटस्थ मार्केटप्लेस है। हम स्वयं प्रॉपर्टी के मालिक नहीं हैं। सभी लेन-देन विक्रेता और खरीदार के बीच होते हैं, BhoomiMitra कनेक्शन को सुगम बनाता है।"
              : "BhoomiMitra is a neutral marketplace connecting buyers and sellers. We do not own the property listed. All transactions occur between seller and buyer; BhoomiMitra facilitates the connection."}
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">3. {lang === "hi" ? "मुफ़्त लिस्टिंग व सशुल्क बूस्ट" : "Free Listings & Paid Boosts"}</h2>
          <p>
            {lang === "hi"
              ? "BhoomiMitra पर प्रॉपर्टी लिस्ट करना 100% मुफ़्त है — कोई सीमा नहीं। वैकल्पिक रूप से उपयोगकर्ता अपनी लिस्टिंग को होमपेज पर लाने के लिए Premium Boost खरीद सकते हैं (मासिक ₹299 या वार्षिक ₹999)। Broker Plan (₹6999/वर्ष) पर अनगिनत पोस्टिंग + Brokers सेक्शन में विशेष स्थान मिलता है। Builder Plan (₹7000/माह) पर सभी लिस्टिंग स्वचालित रूप से Premium होती हैं।"
              : "Listing properties on BhoomiMitra is 100% FREE — no limits. Optionally, users can purchase Premium Boost to feature their listing on the Homepage (Monthly ₹299 or Yearly ₹999). Broker Plan (₹6999/year) offers unlimited posting + dedicated Brokers section. Builder Plan (₹7000/month) makes all listings automatically Premium."}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            {lang === "hi" ? "Enquiries हमेशा मुफ़्त। कोई छिपी लागत नहीं।" : "Enquiries are always free. No hidden charges."}
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">4. {lang === "hi" ? "लिस्टिंग शुद्धता" : "Listing Accuracy"}</h2>
          <p>
            {lang === "hi"
              ? "विक्रेता अपनी लिस्टिंग की सटीकता के लिए ज़िम्मेदार हैं। भ्रामक जानकारी, फ़र्जी दस्तावेज़ या फ़र्जी फ़ोटो BhoomiMitra को बिना धनवापसी के लिस्टिंग हटाने का अधिकार देते हैं।"
              : "Sellers are responsible for the accuracy of their listings. Misleading information, forged documents, or fake photos give BhoomiMitra the right to remove the listing without refund."}
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">5. {lang === "hi" ? "क़ानूनी दस्तावेज़" : "Legal Papers"}</h2>
          <p>
            {lang === "hi"
              ? "प्रॉपर्टी दस्तावेज़ केवल लॉग इन उपयोगकर्ताओं द्वारा वॉटरमार्क वाले पूर्वावलोकन में देखने के लिए उपलब्ध हैं। डाउनलोड, स्क्रीनशॉट या पुनर्वितरण सख्ती से प्रतिबंधित है और IT अधिनियम, 2000 के तहत क़ानूनी कार्रवाई की जा सकती है।"
              : "Property papers are available for view-only, watermarked preview by logged-in users. Download, screenshot, or redistribution is strictly prohibited and may attract legal action under the IT Act, 2000."}
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">6. {lang === "hi" ? "सम्पर्क गोपनीयता" : "Contact Privacy"}</h2>
          <p>
            {lang === "hi"
              ? "विक्रेता के फ़ोन नंबर सार्वजनिक रूप से छिपे रहते हैं। सभी खरीदार पूछताछ BhoomiMitra प्रबंधन से होकर गुज़रती है (+91 70149 65172)। BhoomiMitra को दरकिनार करने के प्रयासों के परिणामस्वरूप खाते को निलंबित किया जा सकता है।"
              : "Seller phone numbers stay hidden from public view. All buyer enquiries route through BhoomiMitra Management (+91 70149 65172). Attempts to bypass BhoomiMitra can lead to account suspension."}
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">7. {lang === "hi" ? "क्षेत्राधिकार" : "Jurisdiction"}</h2>
          <p>
            {lang === "hi"
              ? "इन शर्तों से उत्पन्न विवादों को जोधपुर, राजस्थान की अदालतों के अनन्य क्षेत्राधिकार में हल किया जाएगा। भारतीय क़ानून लागू होते हैं।"
              : "Disputes arising from these terms are resolved in the exclusive jurisdiction of the courts of Jodhpur, Rajasthan. Indian law applies."}
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">8. {lang === "hi" ? "बदलाव" : "Changes"}</h2>
          <p>
            {lang === "hi"
              ? "हम इन शर्तों को अपडेट कर सकते हैं। महत्वपूर्ण बदलावों के लिए, हम पंजीकृत उपयोगकर्ताओं को ईमेल या इन-ऐप नोटिस से सूचित करेंगे।"
              : "We may update these Terms. For material changes, we will notify registered users via email or in-app notice."}
          </p>
        </section>

        <p className="text-xs text-muted-foreground pt-4 border-t border-border">
          {lang === "hi" ? "प्रश्न?" : "Questions?"}{" "}
          <a href="mailto:hello@bhoomimitra.in" className="text-primary underline">hello@bhoomimitra.in</a>
          {" • "}
          <Link to="/privacy" className="text-primary underline">{lang === "hi" ? "गोपनीयता नीति" : "Privacy Policy"}</Link>
        </p>
      </div>
    </div>
  );
}
